import { sortBy } from "lodash";
import { Fragment, MouseEvent, PropsWithChildren, ReactNode } from "react";
import { MdCheckCircle, MdRadioButtonUnchecked } from "react-icons/md";

import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import WarningTooltip from "@components/tooltip/warning-tooltip";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { TopicflowIconMap } from "@components/topicflow-icon/topicflow-icon-helpers";
import { classNames } from "@helpers/css";

export type QuestionListItem = {
  key: string;
  added: boolean;
  // Cannot be added yet, such as Core Values before any are defined.
  unavailable?: boolean;
  row: ReactNode;
};

// Added parts first, then the parts that can be added, then the ones that
// cannot be yet.
const listOrder = ({ added, unavailable }: QuestionListItem) =>
  added ? 0 : unavailable ? 2 : 1;

// The card for each review type on the Questions tab: one row for each part
// of the review. Parts already added come first, so what is in the review
// reads from the top, and parts that cannot be added yet come last. Each
// group keeps the order the review asks them in, with the question set first.
// Children, such as drawers, sit after the rows.
export const QuestionList = ({
  title,
  items,
  children,
}: PropsWithChildren<{ title: string; items: QuestionListItem[] }>) => (
  <section className="flex flex-col gap-1 rounded-md border bg-white px-6 pb-3 pt-5">
    <h3 className="tf-header-base-dark">{title}</h3>
    <div className="flex flex-col">
      {sortBy(items, listOrder).map(({ key, row }) => (
        <Fragment key={key}>{row}</Fragment>
      ))}
    </div>
    {children}
  </section>
);

const stopPropagation = (event: MouseEvent) => event.stopPropagation();

// One part of the review, such as the question set or Core Values. Every part
// is added and removed the same way: "+ Add" while it is not in the review,
// then edit and remove once it is. A check at the start and faded text for
// parts not added show what is in the review at a glance. Edit and remove
// only show on hover or focus, so the card stays quiet. The whole row does
// the main action, so admins do not have to aim for the button.
export const QuestionRow = ({
  title,
  required = false,
  summary,
  warning,
  warningSummary,
  error,
  added,
  disabled,
  onAdd,
  onEdit,
  onRemove,
  addActions,
  actions,
  addAriaLabel,
  editAriaLabel,
  removeAriaLabel,
}: {
  title: string;
  required?: boolean;
  // One line under the title saying what reviewers will be asked.
  summary?: string;
  // Why the questions cannot be used yet. Replaces the buttons when set, and
  // shows in full on hovering the warning icon.
  warning?: string;
  // The warning in a few words, shown in amber under the title.
  warningSummary?: string;
  error?: string;
  added: boolean;
  disabled: boolean;
  // Opens the settings, which add the questions when saved.
  onAdd?: () => void;
  onEdit: () => void;
  onRemove: () => void;
  // Replaces "+ Add", for parts with more than one way in.
  addActions?: ReactNode;
  // More buttons before edit and remove.
  actions?: ReactNode;
  addAriaLabel?: string;
  editAriaLabel?: string;
  removeAriaLabel?: string;
}) => {
  const mainAction = added ? onEdit : onAdd;
  const canClick = !warning && !disabled && !!mainAction;
  return (
    <div
      className={classNames(
        "group -mx-3 flex items-center gap-3 rounded-md px-3 py-2.5",
        canClick && "cursor-pointer hover:bg-gray-50",
      )}
      onClick={canClick ? mainAction : undefined}
    >
      {added ? (
        <MdCheckCircle
          aria-hidden="true"
          className="h-5 w-5 shrink-0 text-green-600"
        />
      ) : (
        <MdRadioButtonUnchecked
          aria-hidden="true"
          className="h-5 w-5 shrink-0 text-gray-300"
        />
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <div
          className={classNames(
            "flex items-center gap-2 text-sm",
            added ? "text-gray-900" : "text-gray-500",
          )}
        >
          {title}
          <span className="sr-only">{added ? "(added)" : "(not added)"}</span>
          {/* Only needed until something is added. */}
          {required && !added && (
            <>
              <span aria-hidden="true" className="-ml-1.5 text-red-600">
                *
              </span>
              <span className="sr-only">(required)</span>
            </>
          )}
        </div>
        {/* Until the warning is resolved, the line says why instead. */}
        {warning && warningSummary && (
          <div className="truncate text-xs text-amber-700" title={warning}>
            {warningSummary}
          </div>
        )}
        {summary && !warning && (
          // One line, so every row is the same height. Hovering shows the rest.
          <div
            className={classNames(
              "truncate text-xs",
              added ? "text-gray-500" : "text-gray-400",
            )}
            title={summary}
          >
            {summary}
          </div>
        )}
        {error && (
          // Found by the form's jump to errors, inside a scrolling column.
          <div data-field-error className="text-xs text-red-600">
            {error}
          </div>
        )}
      </div>
      {/* A fixed height, so a row showing only the warning icon is as tall
          as one with buttons. */}
      <div className="flex h-8 shrink-0 items-center" onClick={stopPropagation}>
        {warning ? (
          <WarningTooltip size={6} text={warning} />
        ) : added ? (
          <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
            {actions}
            <Button
              className="mb-1"
              aria-label={editAriaLabel ?? `Edit ${title}`}
              icon={TopicflowIconMap.edit}
              theme={buttonTheme.iconGray}
              onClick={onEdit}
            />
            <Button
              aria-label={removeAriaLabel ?? `Remove ${title}`}
              disabled={disabled}
              icon={TopicflowIconMap.close}
              theme={buttonTheme.iconGray}
              onClick={onRemove}
            />
          </div>
        ) : (
          (addActions ?? (
            <Button
              theme={buttonTheme.text}
              size={ButtonSize.medium}
              aria-label={addAriaLabel ?? `Add ${title}`}
              disabled={disabled}
              onClick={onAdd}
            >
              <TopicflowIcon type="plus" className="h-4 w-4" />
              Add
            </Button>
          ))
        )}
      </div>
    </div>
  );
};
